import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { useMultiHOA } from '../MultiHOAContext';
import { api } from '../api';

const HOATrackSettings = () => {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  const [hoaSettings, setHoaSettings] = useState({});
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [subscription, setSubscription] = useState(null);

  const effectiveHoaId = activeHoaId || user?.hoa_id;

  useEffect(() => {
    if (effectiveHoaId) {
      loadSettings();
      loadCommittees();
      loadSubscription();
    }
  }, [effectiveHoaId]);

  const loadSettings = async () => {
    if (!effectiveHoaId) return;
    try {
      const response = await api.get('/api/settings/hoa-track', {
        params: { hoa_id: effectiveHoaId }
      });
      setHoaSettings(response.data.settings || {});
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadSubscription = async () => {
    if (!effectiveHoaId) return;
    try {
      const response = await api.get(`/api/hoa/${effectiveHoaId}/settings`);
      if (response.data?.success && response.data?.settings?.subscription) {
        setSubscription(response.data.settings.subscription);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const loadCommittees = async () => {
    if (!effectiveHoaId) return;
    try {
      const response = await api.get(`/api/hoa/${effectiveHoaId}/committees`);
      setCommittees(response.data.committees || []);
    } catch (error) {
      console.error('Failed to load committees:', error);
    }
  };

  const hasAmenityAddon = () => {
    if (!subscription) return false;
    
    // Check if addons array exists and includes amenity_management
    if (Array.isArray(subscription.addons) && 
        subscription.addons.includes('amenity_management')) {
      return true;
    }
    
    // Check if plan.add_ons exists (Stripe format)
    if (subscription.plan?.add_ons && Array.isArray(subscription.plan.add_ons)) {
      return subscription.plan.add_ons.some(addon => 
        addon.addon_id === 'amenity_management' || addon.id === 'amenity_management'
      );
    }
    
    return false;
  };

  const saveSettings = async () => {
    if (!effectiveHoaId) return;
    setLoading(true);
    try {
      await api.post('/api/settings/hoa-track', hoaSettings, {
        params: { hoa_id: effectiveHoaId }
      });
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    setHoaSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateNestedSetting = (parentKey, childKey, value) => {
    setHoaSettings(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const toggleFormEnabled = (formKey) => {
    // Prevent enabling amenity_reservation without the add-on
    if (formKey === 'amenity_reservation' && !hasAmenityAddon()) {
      alert('Amenity Management add-on is required to enable this feature. Please upgrade your subscription in Settings → Subscription.');
      return;
    }
    
    const enabledForms = hoaSettings.enabled_forms || [];
    const newEnabledForms = enabledForms.includes(formKey)
      ? enabledForms.filter(f => f !== formKey)
      : [...enabledForms, formKey];
    
    updateSetting('enabled_forms', newEnabledForms);
  };


  const formOptions = [
    { key: 'arc', name: 'Architectural Request (ARC)', description: 'Property modification requests' },
    { key: 'maintenance', name: 'Maintenance Request', description: 'Common area maintenance issues' },
    { key: 'violation_appeal', name: 'Violation Appeal', description: 'Appeal violation notices' },
    { key: 'pet_registration', name: 'Pet Registration', description: 'Register pets with HOA' },
    { key: 'vehicle_registration', name: 'Vehicle Registration', description: 'Register vehicles for parking' },
    { key: 'amenity_reservation', name: 'Amenity Reservation', description: 'Reserve community amenities' },
    { key: 'general_request', name: 'Contact Board', description: 'Contact the board with inquiries' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">HOA Track Configuration</h3>
        <p className="text-sm text-gray-600">Configure your community's request management system</p>
      </div>

      {/* ARC Email Settings */}
      <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
        <h4 className="text-base font-medium text-blue-900 mb-2">ARC Committee Email</h4>
        <p className="text-sm text-blue-700 mb-3">
          Email address for ARC (Architectural Review Committee) notifications. 
          New ARC requests will be sent to this email automatically.
        </p>
        <input
          type="email"
          placeholder="arc@yourhoa.com"
          value={hoaSettings.arc_email || ''}
          onChange={(e) => updateSetting('arc_email', e.target.value)}
          className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-blue-600 mt-2">
          This email will also be displayed on the Neighborhood tab for residents.
        </p>
      </div>

      {/* Contact Board Email Settings */}
      <div className="border rounded-lg p-4 bg-green-50 border-green-200">
        <h4 className="text-base font-medium text-green-900 mb-2">Contact Board Email</h4>
        <p className="text-sm text-green-700 mb-3">
          Email address for Contact Board notifications. 
          New "Contact Board" requests and status updates will be sent to this email automatically.
        </p>
        <input
          type="email"
          data-testid="contact-board-email-input"
          placeholder="board@yourhoa.com (defaults to HOA community email)"
          value={hoaSettings.contact_board_email || ''}
          onChange={(e) => updateSetting('contact_board_email', e.target.value)}
          className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <p className="text-xs text-green-600 mt-2">
          If left blank, notifications will be sent to your HOA's community email address.
        </p>
      </div>

      {/* After the Fact Request Info */}
      <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
        <h4 className="text-base font-medium text-amber-900 mb-2">After-the-Fact Requests</h4>
        <p className="text-sm text-amber-700">
          When users submit an ARC request and check the "After the Fact Request" checkbox, 
          these requests are for work that was completed previously (often by a previous owner) 
          and the current homeowner is submitting for protective approval. 
          <strong className="text-amber-800"> These requests will NOT be auto-approved</strong> regardless of your auto-approval settings 
          and will always require manual committee review.
        </p>
      </div>

      {/* Form Availability */}
      <div>
        <h4 className="text-base font-medium text-gray-900 mb-4">Available Forms</h4>
        <p className="text-sm text-gray-600 mb-4">Choose which forms are available to community members</p>
        
        <div className="grid md:grid-cols-2 gap-4">
          {formOptions.map(form => {
            const isAmenityForm = form.key === 'amenity_reservation';
            const hasAddon = hasAmenityAddon();
            const isDisabled = isAmenityForm && !hasAddon;
            
            return (
              <div 
                key={form.key} 
                className={`border rounded-lg p-4 ${isAmenityForm ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h5 className={`font-medium ${isAmenityForm ? 'text-indigo-900' : 'text-gray-900'}`}>
                    {form.name}
                    {isAmenityForm && (
                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${hasAddon ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {hasAddon ? 'Add-on Active' : 'Add-on Required'}
                      </span>
                    )}
                  </h5>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={(hoaSettings.enabled_forms || []).includes(form.key)}
                      onChange={() => toggleFormEnabled(form.key)}
                      disabled={isDisabled}
                      className={`mr-2 ${isDisabled ? 'cursor-not-allowed opacity-50' : 'text-indigo-600 focus:ring-indigo-500'}`}
                      title={isDisabled ? 'Amenity Management add-on required' : ''}
                    />
                    <span className={`text-sm ${isDisabled ? 'text-gray-500' : isAmenityForm ? 'text-indigo-700' : 'text-gray-700'}`}>
                      Enabled
                    </span>
                  </label>
                </div>
                <p className={`text-sm ${isAmenityForm ? 'text-indigo-700' : 'text-gray-600'}`}>
                  {form.description}
                  {isDisabled && (
                    <span className="block mt-2 text-xs text-yellow-800 font-medium">
                      💡 To enable this feature, add the Amenity Management add-on in Settings → Subscription.
                    </span>
                  )}
                  {isAmenityForm && hasAddon && (
                    <span className="block mt-2 text-xs text-green-700 font-medium">
                      ✓ Add-on active - Feature automatically enabled
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Group Features */}
      <div>
        <h4 className="text-base font-medium text-gray-900 mb-4">Group Features</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h5 className="font-medium text-gray-900">Committees</h5>
              <p className="text-sm text-gray-600">Enable committee management and assignment</p>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hoaSettings.committees_enabled !== false}
                onChange={(e) => updateSetting('committees_enabled', e.target.checked)}
                className="mr-2 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h5 className="font-medium text-gray-900">Security Team</h5>
              <p className="text-sm text-gray-600">Enable security team assignment and logins</p>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hoaSettings.security_enabled === true}
                onChange={(e) => updateSetting('security_enabled', e.target.checked)}
                className="mr-2 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h5 className="font-medium text-gray-900">Maintenance Team</h5>
              <p className="text-sm text-gray-600">Enable maintenance team assignment and logins</p>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hoaSettings.maintenance_enabled !== false}
                onChange={(e) => updateSetting('maintenance_enabled', e.target.checked)}
                className="mr-2 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
          </div>
        </div>
      </div>

      {/* Auto-Approval Settings */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-base font-medium text-gray-900">Auto Approval System</h4>
            <p className="text-sm text-gray-600">Automatically approve requests after specified days</p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={hoaSettings.auto_approval_enabled === true}
              onChange={(e) => updateSetting('auto_approval_enabled', e.target.checked)}
              className="mr-2 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-900">Enable Auto Approval</span>
          </label>
        </div>
        
        {hoaSettings.auto_approval_enabled && (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {[
              { key: 'arc', name: 'Architectural Requests', default: 30 },
              { key: 'pet_registration', name: 'Pet Registration', default: 7 },
              { key: 'vehicle_registration', name: 'Vehicle Registration', default: 3 },
              { key: 'violation_appeal', name: 'Violation Appeals', default: 14 }
            ].map(item => (
              <div key={item.key} className="border border-gray-200 rounded-lg p-4">
                <div className="font-medium text-gray-900 mb-2">{item.name}</div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={(hoaSettings.auto_approval_days || {})[item.key] || item.default}
                    onChange={(e) => updateNestedSetting('auto_approval_days', item.key, parseInt(e.target.value))}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-sm text-gray-600">days</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto-Archive Settings */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-base font-medium text-gray-900">Auto Archive System</h4>
            <p className="text-sm text-gray-600">Automatically archive approved requests to keep active list clean</p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={hoaSettings.auto_archive_enabled !== false}
              onChange={(e) => updateSetting('auto_archive_enabled', e.target.checked)}
              className="mr-2 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-gray-900">Auto Archive Approved Requests</span>
          </label>
        </div>
        
        {hoaSettings.auto_archive_enabled !== false && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-800">Auto Archive Enabled</h4>
                <p className="text-sm text-green-700 mt-1">
                  When requests are approved by board members, they will automatically be moved to the archive to keep your active requests list clean and organized.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {hoaSettings.auto_archive_enabled === false && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-yellow-800">Manual Archive Required</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Board members must manually archive approved requests. This gives you more control but requires active management to keep your requests organized.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Note about amenity settings */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Amenity Settings Moved</h4>
            <p className="text-sm text-blue-700 mt-1">
              Amenity reservation settings have been moved to <strong>Settings → Amenity Management → Settings tab</strong> for better organization.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div>
          {saveMessage && (
            <span className={`text-sm ${saveMessage.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
              {saveMessage}
            </span>
          )}
        </div>
        <button
          onClick={saveSettings}
          disabled={loading}
          className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {loading ? 'Saving...' : 'Save HOA Track Settings'}
        </button>
      </div>
    </div>
  );
};

export default HOATrackSettings;